import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import { usePaymentStatus } from "@/hooks/usePaymentStatus";

const LOGO = "https://customer-assets.emergentagent.com/job_651751b2-257a-4cae-b051-0e33d311663b/artifacts/mn32dl2p_New%20AFWN%20Logo.png";
const APP_URL = process.env.REACT_APP_APP_URL;
const CTA = "mt-8 inline-flex items-center gap-2 rounded-full font-medium px-8 py-4 bg-afwn-green text-white hover:bg-afwn-greenDark transition-all duration-300";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-afwn-bg text-afwn-navy flex flex-col items-center justify-center px-6 text-center">
    <img src={LOGO} alt="Anxiety Fix Wellness Network" className="h-20 w-20 object-contain rounded-full mb-8" />
    {children}
  </div>
);

const HomeLink = ({ className = "text-afwn-slate hover:text-afwn-green" }) => (
  <div className="mt-4"><Link to="/" className={`text-sm ${className}`}>Back to home</Link></div>
);

const Checking = () => (
  <div data-testid="payment-checking">
    <Loader2 size={48} className="text-afwn-green animate-spin mx-auto" />
    <h1 className="mt-6 font-heading font-light text-3xl">Confirming your payment…</h1>
    <p className="mt-2 text-afwn-slate">Just a moment while we set up your account.</p>
  </div>
);

const Paid = ({ info }) => (
  <div data-testid="payment-success">
    <CheckCircle2 size={56} className="text-afwn-green mx-auto" />
    <h1 className="mt-6 font-heading font-light text-4xl">You're all set! 🎉</h1>
    <p className="mt-3 text-afwn-slate text-lg max-w-md" data-testid="success-plan-text">
      {info.plan_label ? <>Your <strong className="text-afwn-navy">{info.plan_label.replace(/\s*\(.*\)$/, "")}</strong> membership is active</> : "Your subscription is active"} and your 7-day journey begins now. Welcome to the Anxiety Fix Wellness Network.
    </p>
    <a href={info.app_url || APP_URL} data-testid="success-launch-app" className={`${CTA} hover:scale-[1.03]`}>Launch the App <ArrowRight size={18} /></a>
    <HomeLink />
  </div>
);

const Pending = () => (
  <div data-testid="payment-pending">
    <Loader2 size={48} className="text-afwn-amber animate-spin mx-auto" />
    <h1 className="mt-6 font-heading font-light text-3xl">Payment processing…</h1>
    <p className="mt-3 text-afwn-slate max-w-md">This is taking a little longer than usual. If you completed payment, your account will activate shortly — check your email for confirmation.</p>
    <HomeLink className="text-afwn-green hover:underline" />
  </div>
);

const Failed = () => (
  <div data-testid="payment-error">
    <XCircle size={56} className="text-afwn-magenta mx-auto" />
    <h1 className="mt-6 font-heading font-light text-3xl">Something went wrong</h1>
    <p className="mt-3 text-afwn-slate max-w-md">We couldn't confirm your payment. If you were charged, please contact support@anxiety-fix.com and we'll sort it out right away.</p>
    <Link to="/#pricing" data-testid="error-retry" className={CTA}>Back to pricing</Link>
  </div>
);

const VIEWS = { checking: Checking, paid: Paid, pending: Pending, error: Failed };

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { state, info } = usePaymentStatus(params.get("session_id"));
  const View = VIEWS[state];
  return <Shell><View info={info} /></Shell>;
}

export function PaymentCancel() {
  return (
    <Shell>
      <div data-testid="payment-cancel">
        <XCircle size={56} className="text-afwn-slate mx-auto" />
        <h1 className="mt-6 font-heading font-light text-4xl">Checkout cancelled</h1>
        <p className="mt-3 text-afwn-slate text-lg max-w-md">No worries — you weren't charged. Whenever you're ready, your 7-day free trial is waiting.</p>
        <Link to="/#pricing" data-testid="cancel-back-pricing" className={`${CTA} hover:scale-[1.03]`}>Back to Pricing <ArrowRight size={18} /></Link>
        <HomeLink />
      </div>
    </Shell>
  );
}
